// ---------------------------------------------------------------------------
// Noticing that the day has ended.
//
// The server decides this - it refuses the sale whatever the screen believes -
// but the till still has to NOTICE, and promptly, or the cashier builds a whole
// order and is refused at the payment screen with the customer standing there.
//
// The hard part is not midnight. It is all the ways a browser can miss it:
//
//   * the tab was in the background, where timers are throttled to minutes;
//   * the laptop was asleep from 23:30 to 00:30, and every timer that should
//     have fired in between fires late, all at once, or not at all;
//   * the PWA was closed at 23:00 and opened at 08:00, so no timer existed;
//   * the machine's own clock is wrong, so a countdown would be counting to
//     the wrong moment.
//
// So this hook does not trust a timer, and does not trust the local clock to
// decide anything. It arranges to ASK THE SERVER again at every moment the
// answer could have changed: when the page comes back to the foreground, when
// the window regains focus, when the connection returns, at the boundary the
// server itself named, and on a slow tick while anybody is actually looking.
// Every one of those is the same cheap request, and the answer always comes
// from the same rule that refuses the sale.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentShift } from '../api/sales.api';
import { queryKeys } from '../lib/queryClient';

export interface BusinessDayState {
  /// The drawer the cashier is working, if any.
  shift: ReturnType<typeof useCurrentShift>['data'] extends infer T
    ? T extends { shift: infer S }
      ? S
      : never
    : never;
  /// True when a drawer is open but its trading day has passed, so it can no
  /// longer take money. Straight from the server; never computed here.
  dayEnded: boolean;
  /// True while the first answer is still on its way, so the till can avoid
  /// flashing a block at somebody whose shift is perfectly fine.
  loading: boolean;
}

/// How often to re-ask while somebody is watching the screen.
///
/// A minute is far more often than the answer changes - it changes once a day -
/// but it is one small request and it is what makes the block appear without
/// the cashier touching anything. It deliberately does NOT run while the tab
/// is hidden: a background tab polling all night is how a laptop's battery
/// disappears, and the foreground handlers below cover that case anyway.
const WATCHING_INTERVAL_MS = 60_000;

export function useBusinessDay(): BusinessDayState {
  const queryClient = useQueryClient();
  const { data, isLoading } = useCurrentShift();

  const shift = data?.shift ?? null;
  const endsAt = shift?.businessDayEndsAt ?? null;

  useEffect(() => {
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.currentShift });
    };

    // BACK FROM THE BACKGROUND. This is the one that catches the sleeping
    // laptop and the backgrounded PWA, because it fires on resume whether or
    // not any timer survived.
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    // A till that was offline at midnight asks again the moment it is back.
    window.addEventListener('online', refresh);

    const ticker = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, WATCHING_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      window.clearInterval(ticker);
    };
  }, [queryClient]);

  // AND ONE PRECISE WAKE-UP, at the instant the server said this drawer
  // expires. This is what makes the block appear AT midnight rather than up to
  // a minute after it, for the ordinary case of a till sitting on the counter
  // with somebody watching.
  //
  // It is an optimisation, not a guarantee: a timer this long is throttled in
  // a background tab and does not survive sleep at all. Everything above is
  // what makes it safe for this to be unreliable.
  useEffect(() => {
    if (!endsAt) return;

    const delay = new Date(endsAt).getTime() - Date.now();
    // Already past, or so far away that the interval will have re-read it long
    // before then. `setTimeout` is also not to be trusted beyond about 24 days.
    if (delay <= 0 || delay > 25 * 3_600_000) return;

    const timer = window.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.currentShift });
    }, delay + 1_000); // a second past the boundary, so the server agrees

    return () => window.clearTimeout(timer);
  }, [endsAt, queryClient]);

  return {
    shift: shift as BusinessDayState['shift'],
    // Only ever what the server said. Working this out from `Date.now()` here
    // would put the decision on the machine least able to be trusted with it.
    dayEnded: Boolean(shift?.businessDayEnded),
    loading: isLoading,
  };
}
